import React, { useState, useRef, useCallback, useEffect } from 'react';
import { HeadlineGroup } from '../types';
import PanelLayout from './PanelLayout';
import { useAuth } from '../contexts/AuthContext';

interface ResizablePanelsProps {
  onHeadlineGroupSelect: (group: HeadlineGroup) => void;
}

const ResizablePanels: React.FC<ResizablePanelsProps> = ({ onHeadlineGroupSelect }) => {
  // Add state for the divider position with localStorage persistence
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    // Try to get saved width from localStorage, default to 50% if not found
    const savedWidth = localStorage.getItem('leftPanelWidth');
    return savedWidth ? parseFloat(savedWidth) : 50;
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelWidthRef = useRef<number>(leftPanelWidth);
  const { initiateGoogleLogin } = useAuth();
  
  // Update ref whenever leftPanelWidth changes
  useEffect(() => {
    leftPanelWidthRef.current = leftPanelWidth;
  }, [leftPanelWidth]);

  // Set left panel to 100% width on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { // md breakpoint in Tailwind
        setLeftPanelWidth(100);
      } else if (leftPanelWidth === 100) {
        // Reset to saved width or default when returning to desktop
        const savedWidth = localStorage.getItem('leftPanelWidth');
        setLeftPanelWidth(savedWidth ? parseFloat(savedWidth) : 50);
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [leftPanelWidth]);

  // Handle mouse down on the divider
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle touch start on the divider
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle mouse move to resize panels
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;
    
    // Calculate percentage (constrain between 20% and 80%)
    const newLeftPanelWidth = Math.min(Math.max((mouseX / containerWidth) * 100, 20), 80);
    
    // Only update if the change is significant (more than 0.5%)
    if (Math.abs(newLeftPanelWidth - leftPanelWidthRef.current) > 0.5) {
      setLeftPanelWidth(newLeftPanelWidth);
    }
  }, [isDragging]);

  // Handle touch move to resize panels
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !containerRef.current || !e.touches[0]) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const touchX = e.touches[0].clientX - containerRect.left;
    
    // Calculate percentage (constrain between 20% and 80%)
    const newLeftPanelWidth = Math.min(Math.max((touchX / containerWidth) * 100, 20), 80);
    
    // Only update if the change is significant (more than 0.5%)
    if (Math.abs(newLeftPanelWidth - leftPanelWidthRef.current) > 0.5) {
      setLeftPanelWidth(newLeftPanelWidth);
    }
  }, [isDragging]);

  // Handle mouse up to stop dragging and save preference
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Save the current width preference to localStorage using the ref
    localStorage.setItem('leftPanelWidth', leftPanelWidthRef.current.toString());
  }, []); // No dependencies on state

  // Handle touch end to stop dragging
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // Save the current width preference to localStorage using the ref
    localStorage.setItem('leftPanelWidth', leftPanelWidthRef.current.toString());
  }, []); // No dependencies on state

  // Handle double click to reset to default 50/50 split
  const handleDoubleClick = useCallback(() => {
    setLeftPanelWidth(50);
    // leftPanelWidthRef.current will be updated via the useEffect
    localStorage.setItem('leftPanelWidth', '50');
  }, []);

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Check if we're on mobile
  const isMobile = window.innerWidth < 768;

  return (
    <PanelLayout 
      leftPanelWidth={leftPanelWidth}
      containerRef={containerRef}
      isDragging={isDragging}
      handleMouseDown={handleMouseDown}
      handleTouchStart={handleTouchStart}
      handleDoubleClick={handleDoubleClick}
      onHeadlineGroupSelect={onHeadlineGroupSelect}
      showLoginInHeader={true}
      initiateGoogleLogin={initiateGoogleLogin}
    />
  );
};

export default ResizablePanels; 